/**
 * `heysegment validate` — theme + font-system + build validation.
 *
 * Structure mirrors the CLI's core pattern: a composite parent that runs every
 * check, plus isolated subcommands (`validate theme`, `validate fonts`,
 * `validate build`). The runners (`themeAt`/`fontsAt`/`buildAt`) are exported
 * separately from the commands so they're easy to unit-test and reuse. A failing
 * build is a validation failure too, so `build` is a first-class check.
 *
 * @module
 */
import { Console, Effect, FileSystem, Path } from "effect"
import { Command, Flag } from "effect/unstable/cli"
import { ChildProcessSpawner } from "effect/unstable/process"
import { runBuild } from "../build"
import { emitReports } from "../output"
import {
  ASTRO_CONFIG_PATH,
  FONT_MANIFEST_PATH,
  GLOBAL_CSS_PATH,
  LAYOUT_PATH,
  THEME_CSS_PATH,
} from "../paths"
import { readArtifact, resolveProjectDir } from "../project"
import { buildReport, fontsReport, themeReport, type ValidationReport } from "../report"
import type { UsageError } from "../errors"
import { ARCHIVE_EXCLUDES } from "../session/archive"

type Env = FileSystem.FileSystem | Path.Path

/** Read `theme.css` from a resolved project dir and build the theme report. */
export const themeAt = (projectDir: string): Effect.Effect<ValidationReport, UsageError, Env> =>
  readArtifact(projectDir, THEME_CSS_PATH).pipe(Effect.flatMap(themeReport))

/** Read the four font files from a resolved project dir and build the font report. */
export const fontsAt = (projectDir: string): Effect.Effect<ValidationReport, UsageError, Env> =>
  Effect.gen(function* () {
    const [fontManifestJson, astroConfig, layoutAstro, globalCss] = yield* Effect.all([
      readArtifact(projectDir, FONT_MANIFEST_PATH),
      readArtifact(projectDir, ASTRO_CONFIG_PATH),
      readArtifact(projectDir, LAYOUT_PATH),
      readArtifact(projectDir, GLOBAL_CSS_PATH),
    ])
    return yield* fontsReport({ fontManifestJson, astroConfig, layoutAstro, globalCss })
  })

/** Run `corepack pnpm run build` in a resolved project dir and build the build report. */
export const buildAt = (
  projectDir: string,
): Effect.Effect<ValidationReport, UsageError, ChildProcessSpawner.ChildProcessSpawner> =>
  runBuild(projectDir).pipe(Effect.map(buildReport))

// ── Flags (per-command; bind to the command they follow) ─────────────────
const dirFlag = Flag.string("dir").pipe(
  Flag.withDescription("Project directory to validate (default: current directory)"),
  Flag.withDefault("."),
)
const jsonFlag = Flag.boolean("json").pipe(
  Flag.withDescription("Emit a structured JSON result instead of human output"),
)

// ── Subcommands (isolation) ───────────────────────────────────────────────
export const themeCommand = Command.make("theme", { dir: dirFlag, json: jsonFlag }, ({ dir, json }) =>
  resolveProjectDir(dir).pipe(
    Effect.flatMap(themeAt),
    Effect.flatMap((report) => emitReports([report], { json })),
  ),
).pipe(Command.withDescription("Validate src/theme/theme.css against the canonical token set"))

export const fontsCommand = Command.make("fonts", { dir: dirFlag, json: jsonFlag }, ({ dir, json }) =>
  resolveProjectDir(dir).pipe(
    Effect.flatMap(fontsAt),
    Effect.flatMap((report) => emitReports([report], { json })),
  ),
).pipe(Command.withDescription("Validate the font system (manifest, astro config, layout, global css)"))

export const buildCommand = Command.make("build", { dir: dirFlag, json: jsonFlag }, ({ dir, json }) =>
  resolveProjectDir(dir).pipe(
    Effect.flatMap(buildAt),
    Effect.flatMap((report) => emitReports([report], { json })),
  ),
).pipe(Command.withDescription("Build the project (`corepack pnpm run build`); a failing build is a validation failure"))

const emitPushReadyMessage = (json: boolean): Effect.Effect<void> =>
  json
    ? Effect.void
    : Console.log(
        `✓ Push preflight passed. heysegment push will also check version safety and excludes: ${ARCHIVE_EXCLUDES.join(", ")}.`,
      )

// ── Composite (runs every check) ──────────────────────────────────────────
export const validateCommand = Command.make(
  "validate",
  { dir: dirFlag, json: jsonFlag },
  ({ dir, json }) =>
    resolveProjectDir(dir).pipe(
      Effect.flatMap((projectDir) =>
        Effect.all([themeAt(projectDir), fontsAt(projectDir), buildAt(projectDir)]),
      ),
      Effect.flatMap((reports) =>
        emitReports(reports, { json }).pipe(
          Effect.flatMap(() => emitPushReadyMessage(json)),
        ),
      ),
    ),
).pipe(
  Command.withDescription("Run all project validations (theme + fonts + build)"),
  Command.withSubcommands([themeCommand, fontsCommand, buildCommand]),
)
